export interface SearchMenuStrageOptions {
    namespace?: string;
    version?: number;
}

export type SearchMenuPersistedSelections = {
    categories?: string[];
    brands?: string[];
    users?: string[];
    updated_by?: string[];
};

export type SearchMenuPersistedAdvanced = {
    advanced_flags?: string[];
    content_search?: string;
    content_search_fields?: string[];
    rating?: string[];
    product_status?: string[];
    status?: string[];
    updated_at_from?: string;
    updated_at_to?: string;
};

export interface SearchMenuPersistedState {
    version?: number;
    savedAt?: number;
    selections?: SearchMenuPersistedSelections;
    advanced?: SearchMenuPersistedAdvanced;
}

export class SearchMenuStrageUtils {
    public namespace: string;
    public version: number;
    private _keyEnabled: string;
    private _keyState: string;
    private _legacyKeyEnabled: string;
    private _legacyKeyState: string;

    public constructor(options: SearchMenuStrageOptions = {}) {
        this.namespace = String(options.namespace || "products").trim() || "products";
        this.version = Number.isFinite(options.version) ? Number(options.version) : 1;

        this._keyEnabled = `searchMenu:${this.namespace}:remember:v${this.version}`;
        this._keyState = `searchMenu:${this.namespace}:filters:v${this.version}`;
        this._legacyKeyEnabled = `searchMenuPersist:${this.namespace}:enabled`;
        this._legacyKeyState = `searchMenuPersist:${this.namespace}:state:v${this.version}`;
    }

    private _getItem(key: string): string | null {
        try {
            return globalThis?.localStorage?.getItem?.(key) ?? null;
        } catch (_error) {
            return null;
        }
    }

    private _setItem(key: string, value: string): boolean {
        try {
            globalThis?.localStorage?.setItem?.(key, value);
            return true;
        } catch (_error) {
            return false;
        }
    }

    private _removeItem(key: string): void {
        try {
            globalThis?.localStorage?.removeItem?.(key);
        } catch (_error) {
            // ignore
        }
    }

    public isEnabled(): boolean {
        return this._getItem(this._keyEnabled) === "1" || this._getItem(this._legacyKeyEnabled) === "1";
    }

    public setEnabled(enabled: boolean): void {
        if (enabled) {
            this._setItem(this._keyEnabled, "1");
            this._setItem(this._legacyKeyEnabled, "1");
            return;
        }

        this._removeItem(this._keyEnabled);
        this._removeItem(this._legacyKeyEnabled);
        this.clearState();
    }

    public clearState(): void {
        this._removeItem(this._keyState);
        this._removeItem(this._legacyKeyState);
    }

    public loadState(): SearchMenuPersistedState | null {
        const raw = this._getItem(this._keyState) || this._getItem(this._legacyKeyState);
        if (!raw) return null;

        try {
            const obj = JSON.parse(raw) as SearchMenuPersistedState | null;
            if (!obj || typeof obj !== "object") return null;

            const version = Number(obj.version ?? 0);
            if (version !== this.version) return null;

            return obj;
        } catch (_error) {
            return null;
        }
    }

    public saveState(state: Omit<SearchMenuPersistedState, "version" | "savedAt">): void {
        if (!state || typeof state !== "object") return;

        const payload = {
            version: this.version,
            savedAt: Date.now(),
            ...state,
        };

        const json = JSON.stringify(payload);
        this._setItem(this._keyState, json);
        this._setItem(this._legacyKeyState, json);
    }
}
